import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { ExamScore, Student } from '@/types';
import { buildSubjectTrends, type SubjectTrend } from '@/lib/scores';
import { GRADE_GROUPS, gradeGroupOf, type GradeGroup } from '@/lib/grade';
import StudentCardItem from './components/StudentCardItem';
import EndedStudentRow from './components/EndedStudentRow';

const GROUP_STYLE: Record<GradeGroup, { icon: string; chip: string }> = {
  초등학생: { icon: 'ri-seedling-line', chip: 'bg-primary-100 text-primary-700' },
  중학생: { icon: 'ri-book-open-line', chip: 'bg-secondary-100 text-secondary-900' },
  고등학생: { icon: 'ri-graduation-cap-line', chip: 'bg-accent-100 text-accent-800' },
  기타: { icon: 'ri-user-line', chip: 'bg-background-200 text-foreground-700' },
};

export default function StudentsPage() {
  const [students, setStudents] = useState<Student[]>([]);
  const [trends, setTrends] = useState<Record<string, SubjectTrend[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [showEnded, setShowEnded] = useState(false);
  const [reactivatingId, setReactivatingId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [groupFilter, setGroupFilter] = useState<'전체' | GradeGroup>('전체');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [studentsRes, scoresRes] = await Promise.all([
        supabase.from('students').select('*').order('created_at', { ascending: false }),
        supabase.from('exam_scores').select('*'),
      ]);
      if (studentsRes.error) throw studentsRes.error;
      setStudents((studentsRes.data as Student[]) ?? []);

      const grouped: Record<string, ExamScore[]> = {};
      ((scoresRes.data as ExamScore[]) ?? []).forEach((score) => {
        if (!grouped[score.student_id]) grouped[score.student_id] = [];
        grouped[score.student_id].push(score);
      });
      const trendMap: Record<string, SubjectTrend[]> = {};
      Object.keys(grouped).forEach((studentId) => {
        trendMap[studentId] = buildSubjectTrends(grouped[studentId]);
      });
      setTrends(trendMap);
    } catch {
      setError('학생 목록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) =>
        (s.name ?? '').toLowerCase().includes(q) ||
        (s.school ?? '').toLowerCase().includes(q) ||
        (s.target_school ?? '').toLowerCase().includes(q),
    );
  }, [students, query]);

  const activeStudents = useMemo(
    () => students.filter((s) => s.status !== 'ended'),
    [students],
  );
  const endedStudents = useMemo(() => students.filter((s) => s.status === 'ended'), [students]);
  const activeVisible = useMemo(
    () => visible.filter((s) => s.status !== 'ended'),
    [visible],
  );
  const endedVisible = useMemo(() => visible.filter((s) => s.status === 'ended'), [visible]);

  const groupCounts = useMemo(() => {
    const map: Record<GradeGroup, number> = {
      초등학생: 0,
      중학생: 0,
      고등학생: 0,
      기타: 0,
    };
    activeVisible.forEach((s) => {
      map[gradeGroupOf(s.grade)] += 1;
    });
    return map;
  }, [activeVisible]);

  const filteredActive = useMemo(() => {
    if (groupFilter === '전체') return activeVisible;
    return activeVisible.filter((s) => gradeGroupOf(s.grade) === groupFilter);
  }, [activeVisible, groupFilter]);

  const groupedActive = useMemo(
    () =>
      GRADE_GROUPS.map((group) => ({
        group,
        items: filteredActive.filter((s) => gradeGroupOf(s.grade) === group),
      })).filter((g) => g.items.length > 0),
    [filteredActive],
  );

  const reactivate = useCallback(async (id: string) => {
    setReactivatingId(id);
    setError('');
    try {
      const { error: updateError } = await supabase
        .from('students')
        .update({ status: 'active', ended_at: null })
        .eq('id', id);
      if (updateError) throw updateError;
      setStudents((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: 'active', ended_at: null } : s)),
      );
    } catch {
      setError('수업 재개에 실패했어요.');
    } finally {
      setReactivatingId('');
    }
  }, []);

  // 학생과 모든 연관 기록(수업일지·진도·시험·교습비·보충·학부모 공유)을 영구 삭제해요.
  const permanentDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    setError('');
    try {
      const { error: deleteError } = await supabase.from('students').delete().eq('id', id);
      if (deleteError) throw deleteError;
      setStudents((prev) => prev.filter((s) => s.id !== id));
    } catch {
      setError('학생 영구 삭제에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setDeletingId('');
    }
  }, []);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground-950">학생관리카드</h1>
          <p className="mt-1.5 text-sm text-foreground-600">
            학생별 진도·시험·교습비를 카드 하나로 관리해요.
          </p>
        </div>
        <Link
          to="/students/new"
          className="flex items-center gap-1.5 self-start rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          학생 등록
        </Link>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative sm:w-72">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="이름 · 학교 · 목표로 검색"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>
        <p className="text-sm text-foreground-500">
          진행 중 <strong className="font-semibold text-foreground-800">{activeStudents.length}</strong>명
          {endedStudents.length > 0 && (
            <>
              {' · '}종료{' '}
              <strong className="font-semibold text-foreground-800">{endedStudents.length}</strong>명
            </>
          )}
        </p>
      </div>

      {error && !loading && (
        <div className="mt-6 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-100/70 px-4 py-2.5 text-sm text-accent-800">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-accent-300 px-3 py-1 text-xs font-semibold text-accent-800 hover:bg-accent-100 whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
          불러오는 중...
        </div>
      ) : visible.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-dashed border-background-300 bg-background-100 py-20">
          <i className="ri-user-search-line text-3xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">
            {query ? '검색 결과가 없어요.' : '아직 등록된 학생이 없어요.'}
          </p>
          {!query && (
            <Link
              to="/students/new"
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i>
              첫 학생 등록하기
            </Link>
          )}
        </div>
      ) : (
        <>
          {activeVisible.length === 0 ? (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-dashed border-background-300 bg-background-100 py-16">
              <i className="ri-user-line text-3xl text-foreground-400"></i>
              <p className="text-sm text-foreground-600">진행 중인 학생이 없어요.</p>
            </div>
          ) : (
            <>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setGroupFilter('전체')}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap ${
                    groupFilter === '전체'
                      ? 'bg-foreground-900 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-group-line"></i>
                  전체
                  <span className="text-[11px] opacity-80">{activeVisible.length}</span>
                </button>
                {GRADE_GROUPS.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGroupFilter(g)}
                    className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap ${
                      groupFilter === g
                        ? 'bg-foreground-900 text-background-50'
                        : 'border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={GROUP_STYLE[g].icon}></i>
                    {g}
                    <span className="text-[11px] opacity-80">{groupCounts[g]}</span>
                  </button>
                ))}
              </div>

              {filteredActive.length === 0 ? (
                <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-dashed border-background-300 bg-background-100 py-16">
                  <i className="ri-user-line text-3xl text-foreground-400"></i>
                  <p className="text-sm text-foreground-600">해당 그룹의 학생이 없어요.</p>
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-8">
                  {groupedActive.map(({ group, items }) => (
                    <section key={group}>
                      <div className="mb-3 flex items-center gap-2">
                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-lg ${GROUP_STYLE[group].chip}`}
                        >
                          <i className={GROUP_STYLE[group].icon}></i>
                        </span>
                        <h2 className="text-sm font-bold text-foreground-950">{group}</h2>
                        <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-semibold text-foreground-600">
                          {items.length}명
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {items.map((s) => (
                          <StudentCardItem key={s.id} student={s} trends={trends[s.id] ?? []} />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </>
          )}

          {endedVisible.length > 0 && (
            <div className="mt-10">
              <button
                type="button"
                onClick={() => setShowEnded((v) => !v)}
                className="flex w-full items-center justify-between rounded-xl border border-background-200 bg-background-100 px-4 py-3 text-left transition hover:bg-background-200/60"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
                  <i className="ri-archive-2-line text-foreground-500"></i>
                  종료된 학생
                  <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-semibold text-foreground-600">
                    {endedVisible.length}명
                  </span>
                </span>
                <i
                  className={`text-foreground-500 ${
                    showEnded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                  }`}
                ></i>
              </button>
              {showEnded && (
                <div className="mt-3 flex flex-col gap-2">
                  {endedVisible.map((s) => (
                    <EndedStudentRow
                      key={s.id}
                      student={s}
                      busy={reactivatingId === s.id}
                      deleting={deletingId === s.id}
                      onReactivate={reactivate}
                      onDelete={permanentDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}